if (!customElements.get('bundle-showcase')) {
  class BundleShowcase extends HTMLElement {
    constructor() {
      super();
      this.state = {
        top: 'pro',
        tier: 'yearly',
        warranty: false,
      };
    }

    connectedCallback() {
      const defaultTier = this.querySelector('input[name^="bundle-showcase-tier-"]:checked');
      if (defaultTier) this.state.tier = defaultTier.value;
      const defaultTop = this.querySelector('input[name^="bundle-showcase-top-"]:checked');
      if (defaultTop) this.state.top = defaultTop.value;
      this.cacheEls();
      this.bindEvents();
      this.bindMedia();
      this.render();
    }

    cacheEls() {
      this.els = {
        cta: this.querySelector('[data-cta]'),
        ctaTotal: this.querySelector('[data-cta-total]'),
        ctaLabel: this.querySelector('[data-cta-label]'),
        stickyBar: this.querySelector('[data-sticky-bar]'),
        stickyCta: this.querySelector('[data-sticky-cta]'),
        stickySubtitle: this.querySelector('[data-sticky-subtitle]'),
        // A/B "subscription clarity" column. Present only when the section opts in.
        clarityB: this.querySelector('.bundle-showcase__purchase-b'),
        clarityBWidget: this.querySelector('.bundle-showcase__purchase-b bundle-offers-widget'),
        appNote: this.querySelector('[data-app-note]'),
        proPrice: this.querySelector('[data-pro-price]'),
        proCompare: this.querySelector('[data-pro-compare]'),
        proDiscount: this.querySelector('[data-pro-discount]'),
        scannerPriceLabel: this.querySelector('[data-scanner-price-label]'),
        subtotalNow: this.querySelector('[data-subtotal-now]'),
        subtotalCompare: this.querySelector('[data-subtotal-compare]'),
        save: this.querySelector('[data-total-save]'),
        yearlyCompare: this.querySelector('[data-yearly-compare]'),
        yearlySave: this.querySelector('[data-yearly-save]'),
        year2Compare: this.querySelector('[data-year2-compare]'),
        year2Save: this.querySelector('[data-year2-save]'),
        proSave: this.querySelector('[data-pro-save]'),
        triggerTitle: this.querySelector('[data-trigger-title]'),
        triggerNow: this.querySelector('[data-trigger-now]'),
        triggerRibbon: this.querySelector('[data-trigger-ribbon]'),
        triggerCompare: this.querySelector('[data-trigger-compare]'),
        triggerBtn: this.querySelector('[data-tier-trigger]'),
        lineSubscription: this.querySelector('[data-line="subscription"]'),
        lineSubscriptionLabel: this.querySelector('[data-line="subscription"] [data-line-label]'),
        lineSubscriptionPrice: this.querySelector('[data-line="subscription"] [data-line-price]'),
        lineScanner: this.querySelector('[data-line="scanner"]'),
        lineWarranty: this.querySelector('[data-line="warranty"]'),
        warrantyCheckbox: this.querySelector('[data-warranty-checkbox]'),
        proOption: this.querySelector('.bundle-showcase__option-pro'),
        scannerOption: this.querySelector('.bundle-showcase__option-scanner'),
      };
    }

    currentProduct() {
      return this.state.top === 'scanner' ? 'scanner' : this.state.tier;
    }

    tierSavings() {
      // Save = (monthly × tier_months) − tier_price. Monthly tier has no comparison.
      const monthly = this.getPrice('monthlyPrice');
      const yearly = this.getPrice('yearlyPrice');
      const year2 = this.getPrice('year2Price');
      const yearlyCompare = monthly * 12;
      const year2Compare = monthly * 24;
      return {
        yearlyCompare,
        year2Compare,
        yearlySave: Math.max(0, yearlyCompare - yearly),
        year2Save: Math.max(0, year2Compare - year2),
        currentTierCompare: this.state.tier === 'yearly' ? yearlyCompare : this.state.tier === '2year' ? year2Compare : monthly,
        currentTierSave: this.state.tier === 'yearly' ? Math.max(0, yearlyCompare - yearly) : this.state.tier === '2year' ? Math.max(0, year2Compare - year2) : 0,
      };
    }

    bindEvents() {
      this.querySelectorAll('input[name^="bundle-showcase-top-"]').forEach(el => {
        el.addEventListener('change', () => {
          this.state.top = el.value;
          this.render();
        });
      });
      this.querySelectorAll('input[name^="bundle-showcase-tier-"]').forEach(el => {
        el.addEventListener('change', () => {
          this.state.tier = el.value;
          // selecting a tier implies the PRO option
          this.state.top = 'pro';
          const topRadio = this.querySelector('input[name^="bundle-showcase-top-"][value="pro"]');
          if (topRadio) topRadio.checked = true;
          // Auto-close the mobile dropdown when a tier is picked
          this.dataset.mobilePanel = 'closed';
          if (this.els.triggerBtn) this.els.triggerBtn.setAttribute('aria-expanded', 'false');
          this.render();
        });
      });
      if (this.els.triggerBtn) {
        this.els.triggerBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = this.dataset.mobilePanel === 'open';
          this.dataset.mobilePanel = isOpen ? 'closed' : 'open';
          this.els.triggerBtn.setAttribute('aria-expanded', String(!isOpen));
        });
      }
      if (this.els.warrantyCheckbox) {
        this.els.warrantyCheckbox.addEventListener('change', () => {
          this.state.warranty = this.els.warrantyCheckbox.checked;
          this.render();
        });
      }
      if (this.els.cta) {
        this.els.cta.addEventListener('click', () => this.addToCart());
      }
      // When the B column is live the sticky subtitle follows ITS selection, so it
      // has to re-render when the visitor switches card there. The B radios drive
      // the shared product form via bundle-offers-selector.js; we only read them.
      if (this.els.clarityBWidget) {
        this.els.clarityBWidget.querySelectorAll('input[name="purchase_option_name"]').forEach((input) => {
          input.addEventListener('change', () => this.render());
        });
      }
      if (this.els.stickyBar) {
        this.els.stickyBar.querySelectorAll('a[href^="#"]').forEach((link) => {
          link.addEventListener('click', (e) => {
            const href = link.getAttribute('href') || '';
            const target = document.querySelector(href);
            if (!target) return;
            e.preventDefault();
            const header = document.querySelector('.shopify-section-group-header-group, header');
            const offset = header ? header.getBoundingClientRect().height : 0;
            const top = target.getBoundingClientRect().top + window.scrollY - offset - 12;
            window.scrollTo({ top, behavior: 'smooth' });
          });
        });
      }
      if (this.els.stickyBar) {
        const footer = document.querySelector('.shopify-section-group-footer-group, .footer, footer');
        // The fake PDP card. Use the inner grid (not <bundle-showcase> itself)
        // so the bounding rect ignores the fixed-position sticky bar that lives
        // inside the same custom element.
        const bundle = this.querySelector('.bundle-showcase__grid');
        const bar = this.els.stickyBar;
        const update = () => {
          const atTop = window.scrollY < 80;
          const overFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
          let overBundle = false;
          if (bundle) {
            const rect = bundle.getBoundingClientRect();
            overBundle = rect.top < window.innerHeight && rect.bottom > 0;
          }
          const shouldShow = !atTop && !overFooter && !overBundle;
          // Defer the class flip to the next frame so the browser commits the
          // current (hidden) styles first and the transition runs cleanly.
          requestAnimationFrame(() => {
            bar.classList.toggle('is-revealed', shouldShow);
            const cookiebot = document.getElementById('CookiebotWidget');
            if (cookiebot) cookiebot.style.display = shouldShow ? 'none' : '';
          });
        };
        this._stickyUpdate = update;
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
      }
    }

    disconnectedCallback() {
      if (this._stickyUpdate) {
        window.removeEventListener('scroll', this._stickyUpdate);
        window.removeEventListener('resize', this._stickyUpdate);
        this._stickyUpdate = null;
      }
    }

    bindMedia() {
      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxSlides = this.lightbox ? Array.from(this.lightbox.querySelectorAll('[data-lb-slide]')) : [];
      this.lightboxIndex = 0;

      this.querySelectorAll('[data-media-tile]').forEach((tile) => {
        tile.addEventListener('click', (e) => {
          if (e.target.closest('[data-toggle]')) return;
          e.preventDefault();
          this.openLightbox(tile.dataset.mediaId);
        });
        tile.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (e.target.closest('[data-toggle]')) return;
          e.preventDefault();
          this.openLightbox(tile.dataset.mediaId);
        });
      });

      this.querySelectorAll('[data-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const tile = btn.closest('[data-state]');
          if (!tile) return;
          const video = tile.querySelector('video');
          if (!video) return;
          if (video.paused) {
            try { video.play(); } catch (err) {}
          } else {
            video.pause();
          }
        });
      });

      this.querySelectorAll('[data-media-tile][data-state]').forEach((tile) => {
        const video = tile.querySelector('video');
        if (!video) return;
        video.playsInline = true;
        video.addEventListener('play', () => { tile.dataset.state = 'playing'; });
        video.addEventListener('pause', () => { tile.dataset.state = 'paused'; });
        video.addEventListener('ended', () => { tile.dataset.state = 'paused'; });
      });

      if (!this.lightbox) return;
      const prev = this.lightbox.querySelector('[data-lb-prev]');
      const next = this.lightbox.querySelector('[data-lb-next]');
      const close = this.lightbox.querySelector('[data-lb-close]');
      if (prev) prev.addEventListener('click', () => this.stepLightbox(-1));
      if (next) next.addEventListener('click', () => this.stepLightbox(1));
      if (close) close.addEventListener('click', () => this.closeLightbox());
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) this.closeLightbox();
      });
      this.lightbox.addEventListener('close', () => this.pauseLightboxVideos());
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        else if (e.key === 'ArrowRight') this.stepLightbox(1);
      });
    }

    openLightbox(mediaId) {
      if (!this.lightbox || !this.lightboxSlides.length) return;
      const idx = this.lightboxSlides.findIndex((s) => s.dataset.mediaId === mediaId);
      this.lightboxIndex = idx >= 0 ? idx : 0;
      this.showLightboxSlide();
      if (typeof this.lightbox.showModal === 'function') {
        this.lightbox.showModal();
      } else {
        this.lightbox.setAttribute('open', '');
      }
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.pauseLightboxVideos();
      if (typeof this.lightbox.close === 'function') {
        this.lightbox.close();
      } else {
        this.lightbox.removeAttribute('open');
      }
    }

    stepLightbox(delta) {
      if (!this.lightboxSlides.length) return;
      const len = this.lightboxSlides.length;
      this.lightboxIndex = (this.lightboxIndex + delta + len) % len;
      this.showLightboxSlide();
    }

    showLightboxSlide() {
      this.pauseLightboxVideos();
      this.lightboxSlides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.lightboxIndex);
      });
    }

    pauseLightboxVideos() {
      if (!this.lightbox) return;
      this.lightbox.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (e) {} });
    }

    getPrice(key) {
      const v = this.dataset[key];
      return v ? parseInt(v, 10) : 0;
    }

    selectedTierPrice() {
      if (this.state.tier === 'monthly') return this.getPrice('monthlyPrice');
      if (this.state.tier === 'yearly') return this.getPrice('yearlyPrice');
      if (this.state.tier === '2year') return this.getPrice('year2Price');
      return 0;
    }

    selectedTierVariantId() {
      if (this.state.tier === 'monthly') return this.dataset.monthlyId;
      if (this.state.tier === 'yearly') return this.dataset.yearlyId;
      if (this.state.tier === '2year') return this.dataset.year2Id;
      return null;
    }

    selectedTierSellingPlanId() {
      if (this.state.tier === 'monthly') return this.dataset.monthlyPlan;
      if (this.state.tier === 'yearly') return this.dataset.yearlyPlan;
      if (this.state.tier === '2year') return this.dataset.year2Plan;
      return null;
    }

    selectedTierCompareAt() {
      // Shopify `compare_at_price` of the subscription product for the
      // currently-selected tier. Used by the discount-percentage pill so the
      // value reflects the merchant-set sale price, not a synthesized
      // monthly × N comparison.
      if (this.state.tier === 'monthly') return this.getPrice('monthlyCompareAt');
      if (this.state.tier === 'yearly') return this.getPrice('yearlyCompareAt');
      if (this.state.tier === '2year') return this.getPrice('year2CompareAt');
      return 0;
    }

    selectedTierLabel() {
      const label = this.querySelector(`.bundle-showcase__tier[data-tier="${this.state.tier}"] .bundle-showcase__tier-title`);
      return label ? label.textContent.trim() : '';
    }

    computeTotal() {
      const scanner = this.getPrice('scannerPrice');
      const warranty = this.state.warranty ? this.getPrice('warrantyPrice') : 0;
      const sub = this.state.top === 'pro' ? this.selectedTierPrice() : 0;
      return { scanner, warranty, sub, total: scanner + warranty + sub };
    }

    // The B column is live only when it is actually visible in layout, since the
    // experiment reveals it by flipping CSS on an ancestor. This is the same test
    // bundle-offers-selector.js:85 uses to decide which A/B widget instance owns the
    // shared product form, so the sticky bar and the form can never disagree about
    // which variant the visitor is looking at.
    _clarityBActive() {
      return !!this.els.clarityB && this.els.clarityB.getClientRects().length > 0;
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return format
        .replace(/\{\{\s*amount\s*\}\}/, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)));
    }

    formatMoneyInt(cents) {
      // Same as formatMoney but always renders the integer amount (no decimals).
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = String(Math.round(cents / 100));
      return format
        .replace(/\{\{\s*amount(_no_decimals)?(_with_comma_separator)?\s*\}\}/, value);
    }

    render() {
      // Toggle selected classes
      this.dataset.topSelection = this.state.top;
      this.els.proOption.classList.toggle('bundle-showcase__option--selected', this.state.top === 'pro');
      if (this.els.scannerOption) {
        this.els.scannerOption.classList.toggle('bundle-showcase__option--selected', this.state.top === 'scanner');
      }
      this.querySelectorAll('.bundle-showcase__tier').forEach(t => {
        t.classList.toggle('bundle-showcase__tier--selected', t.dataset.tier === this.state.tier);
      });

      const { scanner, sub, total } = this.computeTotal();
      const savings = this.tierSavings();
      const appsOnly = this.hasAttribute('data-apps-only');

      // Per-tier compare-at + save badges (computed from monthly × tier_months).
      // In apps-only mode these are suppressed in favor of an inline per-month
      // figure (rendered next to the price) and a "X if paying monthly" foot.
      const setCompare = (el, cents) => {
        if (!el) return;
        if (cents > 0) {
          el.textContent = this.formatMoney(cents);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      };
      const saveTpl = this.dataset.i18nSaveAmount || 'Save __AMOUNT__';
      const setSave = (el, cents) => {
        if (!el) return;
        if (cents > 0) {
          el.textContent = saveTpl.replace('__AMOUNT__', this.formatMoney(cents));
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      };
      if (appsOnly) {
        // Strikethrough is replaced by the "if paying monthly" foot line,
        // but we keep the "Save £X" pill so the value is still highlighted.
        if (this.els.yearlyCompare) this.els.yearlyCompare.hidden = true;
        if (this.els.year2Compare) this.els.year2Compare.hidden = true;
        setSave(this.els.yearlySave, savings.yearlySave);
        setSave(this.els.year2Save, savings.year2Save);
      } else {
        setCompare(this.els.yearlyCompare, savings.yearlyCompare);
        setSave(this.els.yearlySave, savings.yearlySave);
        setCompare(this.els.year2Compare, savings.year2Compare);
        setSave(this.els.year2Save, savings.year2Save);
      }

      // Apps-only: render the per-month equivalent inline next to each
      // tier price (e.g. "£41.99 · £4/month"). The "·" separator is a CSS
      // pseudo-element so we only emit the price portion here.
      const permonthTpl = this.dataset.i18nPermonthInline || '__PRICE__/month';
      this.querySelectorAll('[data-tier-permonth]').forEach((el) => {
        if (!appsOnly) { el.hidden = true; return; }
        const tier = el.dataset.tierPermonth;
        const months = tier === '2year' ? 24 : 12;
        const tierCents = tier === '2year' ? this.getPrice('year2Price') : this.getPrice('yearlyPrice');
        if (!tierCents) { el.hidden = true; return; }
        const perMonth = Math.round(tierCents / months);
        el.textContent = permonthTpl.replace('__PRICE__', this.formatMoneyInt(perMonth));
        el.hidden = false;
      });

      // Inline "Save €X" pill on the PRO bundle row (mobile shows this; desktop is hidden)
      if (this.els.proSave) {
        if (this.state.top === 'pro' && savings.currentTierSave > 0) {
          this.els.proSave.textContent = saveTpl.replace('__AMOUNT__', this.formatMoney(savings.currentTierSave));
          this.els.proSave.hidden = false;
        } else {
          this.els.proSave.hidden = true;
        }
      }

      // Mobile dropdown trigger reflects the current tier
      if (this.els.triggerTitle) this.els.triggerTitle.textContent = this.selectedTierLabel();
      if (this.els.triggerNow) this.els.triggerNow.textContent = this.formatMoney(this.selectedTierPrice());
      if (this.els.triggerCompare) {
        const compareCents = this.state.tier === 'yearly' ? savings.yearlyCompare : this.state.tier === '2year' ? savings.year2Compare : 0;
        if (compareCents > 0 && compareCents !== this.selectedTierPrice()) {
          this.els.triggerCompare.textContent = this.formatMoney(compareCents);
          this.els.triggerCompare.hidden = false;
        } else {
          this.els.triggerCompare.hidden = true;
        }
      }
      // Mirror the selected tier's ribbon (Most Popular / Best Value) on the
      // closed mobile trigger so the merchant's emphasis is still visible.
      if (this.els.triggerRibbon) {
        const sourceTier = this.querySelector(`.bundle-showcase__tier[data-tier="${this.state.tier}"]`);
        const sourceRibbon = sourceTier ? sourceTier.querySelector('.bundle-showcase__tier-ribbon') : null;
        if (sourceRibbon) {
          this.els.triggerRibbon.textContent = sourceRibbon.textContent.trim();
          this.els.triggerRibbon.className = 'bundle-showcase__tier-trigger-ribbon ' + Array.from(sourceRibbon.classList)
            .filter((c) => c.startsWith('bundle-showcase__tier-ribbon--'))
            .map((c) => c.replace('tier-ribbon--', 'tier-trigger-ribbon--'))
            .join(' ');
          this.els.triggerRibbon.dataset.triggerRibbon = '';
          this.els.triggerRibbon.hidden = false;
        } else {
          this.els.triggerRibbon.hidden = true;
        }
      }

      // Pro card price now = scanner + selected tier. Compare-at usually =
      // scanner + (monthly × tier_months), but in apps-only mode we instead
      // surface the merchant-configured `compare_at_price` on the
      // subscription product itself (e.g. €68.95 / €215.40).
      if (this.els.proPrice) this.els.proPrice.textContent = this.formatMoney(scanner + this.selectedTierPrice());
      const tierCompareAt = this.selectedTierCompareAt();
      const tierNow = this.selectedTierPrice();
      const useAppsOnlyCompare = appsOnly && tierCompareAt > tierNow;
      const proCompareCents = useAppsOnlyCompare ? tierCompareAt : (scanner + savings.currentTierCompare);
      const proCompareSave = useAppsOnlyCompare ? (tierCompareAt - tierNow) : savings.currentTierSave;
      if (this.els.proCompare) {
        if (this.state.top === 'pro' && proCompareSave > 0) {
          this.els.proCompare.textContent = this.formatMoney(proCompareCents);
          this.els.proCompare.hidden = false;
        } else {
          this.els.proCompare.hidden = true;
        }
      }
      // Discount-percentage pill: apps-only mode only, computed from the
      // subscription product's own compare_at_price (Shopify-configured sale
      // price). In bundle mode the bundle savings already surface via the
      // existing "Save €X" pill, so we don't show a percentage there.
      if (this.els.proDiscount) {
        if (appsOnly && this.state.top === 'pro' && tierCompareAt > tierNow && tierCompareAt > 0) {
          const pct = Math.round(((tierCompareAt - tierNow) / tierCompareAt) * 100);
          this.els.proDiscount.textContent = `-${pct}%`;
          this.els.proDiscount.hidden = false;
        } else {
          this.els.proDiscount.hidden = true;
        }
      }

      // Update line items
      if (this.els.lineSubscription) {
        if (sub > 0) {
          this.els.lineSubscription.hidden = false;
          this.els.lineSubscriptionLabel.textContent = this.selectedTierLabel();
          this.els.lineSubscriptionPrice.textContent = this.formatMoney(sub);
        } else {
          this.els.lineSubscription.hidden = true;
        }
      }
      if (this.els.lineScanner) this.els.lineScanner.hidden = scanner === 0;
      if (this.els.lineWarranty) this.els.lineWarranty.hidden = !this.state.warranty;

      // Subtotal + "you save"
      const totalSave = this.state.top === 'pro' ? savings.currentTierSave : 0;
      const compareTotal = total + totalSave;
      this.dataset.hasSavings = totalSave > 0 ? 'true' : 'false';
      if (this.els.subtotalNow) this.els.subtotalNow.textContent = this.formatMoney(total);
      if (this.els.subtotalCompare) {
        if (totalSave > 0) {
          this.els.subtotalCompare.textContent = this.formatMoney(compareTotal);
          this.els.subtotalCompare.hidden = false;
        } else {
          this.els.subtotalCompare.hidden = true;
        }
      }
      if (this.els.ctaTotal) this.els.ctaTotal.textContent = this.formatMoney(total);

      // Compute the per-month app price for the currently-selected tier.
      // Yearly/2-year products are priced as the total commitment, so divide
      // to get the equivalent per-month figure.
      const tierMonths = this.state.tier === 'yearly' ? 12 : this.state.tier === '2year' ? 24 : 1;
      const perMonthCents = Math.round(this.selectedTierPrice() / tierMonths);
      const perMonthMoney = this.formatMoneyInt(perMonthCents);

      // Sticky bar subtitle: localized template from data-i18n-* attributes
      // (set in Liquid via `| t`). Placeholders `__SCANNER__` and `__PERMONTH__`
      // are replaced with the formatted money values at render time.
      if (this.els.stickySubtitle && this.els.stickyBar) {
        const money = (cents) => `<span class="bundle-showcase__sticky-price">${this.formatMoney(cents)}</span>`;
        const scannerStr = money(scanner);
        const perMonthStr = `<span class="bundle-showcase__sticky-price">${perMonthMoney}</span>`;

        if (this._clarityBActive()) {
          // The B column sells the single bundle product, so the control's
          // "scanner + X/month" framing describes an offer that is not on screen.
          // Prices come straight off the widget's data-* attributes, which are the
          // same values its own cards render, rather than being recomputed here.
          const w = this.els.clarityBWidget;
          const oneTime = w?.querySelector('input[value="one-time"]')?.checked === true;
          if (oneTime) {
            const cents = parseInt(w?.dataset.basePrice, 10) || scanner;
            this.els.stickySubtitle.innerHTML = (
              this.els.stickyBar.dataset.i18nScannerOnly || `Scanner __SCANNER__`
            ).replace('__SCANNER__', money(cents));
          } else {
            const cents = parseInt(w?.dataset.offerPrice, 10) || 0;
            this.els.stickySubtitle.innerHTML = (
              this.els.stickyBar.dataset.i18nBundle || `__PRICE__`
            ).replace('__PRICE__', money(cents));
          }
        } else {
          let template;
          if (appsOnly) {
            template = this.els.stickyBar.dataset.i18nAppsOnly || `From __PERMONTH__/month`;
          } else if (this.state.top === 'scanner') {
            template = this.els.stickyBar.dataset.i18nScannerOnly || `Scanner __SCANNER__`;
          } else {
            template = this.els.stickyBar.dataset.i18nWithSub || `Scanner __SCANNER__ + __PERMONTH__/month for the app`;
          }
          this.els.stickySubtitle.innerHTML = template
            .replace('__SCANNER__', scannerStr)
            .replace('__PERMONTH__', perMonthStr);
        }
      }

      // PRO app-note: localized template from data-i18n-app-note on the
      // <bundle-showcase> element; __PRICE__ swapped for the per-month figure.
      if (this.els.appNote) {
        const template = this.dataset.i18nAppNote || 'Incl. app at __PRICE__/mo';
        this.els.appNote.textContent = template.replace('__PRICE__', perMonthMoney);
      }

      // Per-tier footnote - localized template; format varies by mode.
      // Bundle mode: "Subscription only €X/month" on yearly and 2-year.
      // Apps-only mode: monthly shows "Pay €X more vs yearly"; yearly and
      //   2-year show "€X if paying monthly" (the monthly × N equivalent).
      const tierFootTemplate = this.dataset.i18nTierFoot || 'Subscription only __PRICE__/month';
      const monthlyVsYearlyTpl = this.dataset.i18nMonthlyVsYearly || 'Pay __AMOUNT__ more vs yearly';
      const ifPayingMonthlyTpl = this.dataset.i18nIfPayingMonthly || '__AMOUNT__ if paying monthly';
      this.querySelectorAll('[data-tier-foot]').forEach((el) => {
        const tier = el.dataset.tierFoot;

        if (appsOnly) {
          const monthlyCents = this.getPrice('monthlyPrice');
          if (tier === 'monthly') {
            const yearlyCents = this.getPrice('yearlyPrice');
            const diff = monthlyCents * 12 - yearlyCents;
            if (diff > 0 && yearlyCents > 0) {
              el.textContent = monthlyVsYearlyTpl.replace('__AMOUNT__', this.formatMoneyInt(diff));
            } else {
              el.textContent = '';
            }
            return;
          }
          if (tier === 'yearly' || tier === '2year') {
            const months = tier === '2year' ? 24 : 12;
            if (!monthlyCents) { el.textContent = ''; return; }
            const equivalent = monthlyCents * months;
            el.textContent = ifPayingMonthlyTpl.replace('__AMOUNT__', this.formatMoney(equivalent));
            return;
          }
        }

        const months = tier === 'yearly' ? 12 : tier === '2year' ? 24 : 1;
        const tierCents = tier === 'yearly' ? this.getPrice('yearlyPrice')
          : tier === '2year' ? this.getPrice('year2Price')
          : this.getPrice('monthlyPrice');
        if (!tierCents) { el.textContent = ''; return; }
        const perMonth = Math.round(tierCents / months);
        el.textContent = tierFootTemplate.replace('__PRICE__', this.formatMoneyInt(perMonth));
      });
      if (this.els.save) {
        const saveRow = this.els.save.closest('.bundle-showcase__totals-row');
        if (totalSave > 0) {
          if (saveRow) saveRow.hidden = false;
          this.els.save.textContent = `-${this.formatMoney(totalSave)}`;
        } else if (saveRow) {
          saveRow.hidden = true;
        }
      }
    }

    async addToCart() {
      const items = [];
      if (this.dataset.scannerId) items.push({ id: this.dataset.scannerId, quantity: 1 });
      if (this.state.top === 'pro') {
        const subId = this.selectedTierVariantId();
        if (subId) {
          const sub = { id: subId, quantity: 1 };
          const planId = this.selectedTierSellingPlanId();
          if (planId) sub.selling_plan = planId;
          items.push(sub);
        }
      }
      // When the scanner is added together with a subscription, tag both with
      // the same _bundleId so the cart groups them into a single line item
      // with the subscription toggle (matches the PDP bundle pattern in
      // snippets/subscription-widget.liquid).
      if (items.length > 1) {
        const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        items[0].properties = { ...(items[0].properties || {}), _bundleId: bundleId };
        items[1].properties = { ...(items[1].properties || {}), _bundleId: bundleId };
      }
      // Warranty is added as a line-item property on the scanner (same pattern
      // as the PDP / cart line-item warranty toggle), not as a separate product.
      if (this.state.warranty && items[0]) {
        items[0].properties = { ...(items[0].properties || {}), _warranty: 'true' };
      }
      if (!items.length) return;

      this.els.cta.disabled = true;
      const originalLabel = this.els.ctaLabel ? this.els.ctaLabel.textContent : '';
      if (this.els.ctaLabel) this.els.ctaLabel.textContent = 'Adding…';

      try {
        const res = await fetch(this.dataset.cartUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('Cart add failed');

        // Try to open the theme's cart drawer if present; otherwise go to /cart.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else if (drawer && drawer.tagName.toLowerCase() === 'cart-drawer') {
          // dispatch open event
          drawer.dispatchEvent(new CustomEvent('open'));
        } else {
          window.location.href = '/cart';
        }
      } catch (e) {
        console.error(e);
        if (this.els.ctaLabel) this.els.ctaLabel.textContent = originalLabel;
      } finally {
        this.els.cta.disabled = false;
        if (this.els.ctaLabel && this.els.ctaLabel.textContent === 'Adding…') {
          this.els.ctaLabel.textContent = originalLabel;
        }
      }
    }
  }

  customElements.define('bundle-showcase', BundleShowcase);
}
